import React, { useState, useEffect, useRef } from 'react';
import FlipClassService, {
  PreQuizQuestion,
  FlashQuizSession,
  FlashQuizResults,
  InClassQuiz,
  InClassQuizResults,
} from '../../_services/FlipClassService';

interface FlashQuizManagerProps {
  stepId: number;
  batchId: number;
  isOpen: boolean;
  onClose: () => void;
  onQuizComplete?: () => void;
}

type ViewMode = 'questions' | 'active' | 'results' | 'history';

const FlashQuizManager: React.FC<FlashQuizManagerProps> = ({
  stepId,
  batchId,
  isOpen,
  onClose,
  onQuizComplete,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('questions');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Question selection
  const [questions, setQuestions] = useState<PreQuizQuestion[]>([]);
  const [testInfo, setTestInfo] = useState<{ testId: number; testName: string } | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<PreQuizQuestion | null>(null);
  const [duration, setDuration] = useState(60);

  // Active session
  const [activeSession, setActiveSession] = useState<FlashQuizSession | null>(null);
  const [liveResults, setLiveResults] = useState<FlashQuizResults | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  // History
  const [history, setHistory] = useState<any[]>([]);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadQuestions();
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isOpen, stepId, batchId]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await FlipClassService.getPreQuizQuestions(stepId, batchId);

      if (response.success && response.data) {
        setQuestions(response.data.questions || []);
        setTestInfo({
          testId: response.data.testId,
          testName: response.data.testName,
        });
      }
    } catch (err: any) {
      console.error('Failed to load questions:', err);
      setError('Failed to load pre-quiz questions');
    } finally {
      setLoading(false);
    }
  };

  const flashQuestion = async () => {
    if (!selectedQuestion || !testInfo) return;

    try {
      setLoading(true);
      setError(null);

      const response = await FlipClassService.flashQuestion(
        stepId,
        batchId,
        testInfo.testId,
        selectedQuestion.sectionId,
        selectedQuestion.questionId,
        duration
      );

      if (response.success && response.data) {
        setActiveSession(response.data);
        setRemainingTime(duration);
        setViewMode('active');
        startPolling(response.data.sessionId);
        startTimer();
      }
    } catch (err: any) {
      console.error('Failed to flash question:', err);
      setError('Failed to flash question to class');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (sessionId: number) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Immediate first poll
    pollResults(sessionId);

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(() => {
      pollResults(sessionId);
    }, 2000);
  };

  const pollResults = async (sessionId: number) => {
    try {
      const response = await FlipClassService.getFlashQuizResults(sessionId);
      if (response.success && response.data) {
        setLiveResults(response.data);

        // Stop polling if quiz is closed
        if (!response.data.isActive) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          setViewMode('results');
        }
      }
    } catch (err) {
      console.error('Failed to poll results:', err);
    }
  };

  const startTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const closeQuiz = async () => {
    if (!activeSession) return;

    try {
      setLoading(true);
      await FlipClassService.closeFlashQuiz(activeSession.sessionId);

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      // Final poll to get complete results
      await pollResults(activeSession.sessionId);
      setViewMode('results');
      onQuizComplete?.();
    } catch (err: any) {
      console.error('Failed to close quiz:', err);
      setError('Failed to close quiz');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await FlipClassService.getFlashQuizHistory(stepId, batchId);
      if (response.success && response.data) {
        setHistory(response.data);
        setViewMode('history');
      }
    } catch (err: any) {
      console.error('Failed to load history:', err);
      setError('Failed to load quiz history');
    } finally {
      setLoading(false);
    }
  };

  const resetToQuestions = () => {
    setViewMode('questions');
    setActiveSession(null);
    setLiveResults(null);
    setSelectedQuestion(null);
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
  };

  if (!isOpen) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flash-quiz-overlay" onClick={onClose}>
      <div className="flash-quiz-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flash-quiz-header">
          <h2>
            {viewMode === 'questions' && 'Flash a Question'}
            {viewMode === 'active' && 'Live Quiz Session'}
            {viewMode === 'results' && 'Quiz Results'}
            {viewMode === 'history' && 'Quiz History'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            x
          </button>
        </div>

        {error && (
          <div className="flash-quiz-error">
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* Question Selection View */}
        {viewMode === 'questions' && (
          <div className="flash-quiz-content">
            {loading ? (
              <div className="loading">Loading pre-quiz questions...</div>
            ) : questions.length === 0 ? (
              <div className="no-questions">
                <p>No pre-quiz questions found for this step.</p>
                <p>Make sure a baseline quiz is assigned to this step.</p>
              </div>
            ) : (
              <>
                <div className="quiz-info">
                  <span className="quiz-name">{testInfo?.testName}</span>
                  <button className="history-btn" onClick={loadHistory}>
                    View History
                  </button>
                </div>

                <div className="question-list">
                  {questions.map((q, idx) => (
                    <div
                      key={q.questionId}
                      className={`question-card ${selectedQuestion?.questionId === q.questionId ? 'selected' : ''}`}
                      onClick={() => setSelectedQuestion(q)}
                    >
                      <div className="question-header">
                        <span className="question-num">Q{q.questionNumber}</span>
                        <span className="question-concept">{q.concept}</span>
                        <span
                          className={`question-stat ${
                            (q.preQuizStats?.pctCorrect || 0) < 50
                              ? 'low'
                              : (q.preQuizStats?.pctCorrect || 0) < 70
                              ? 'medium'
                              : 'high'
                          }`}
                        >
                          {q.preQuizStats?.pctCorrect !== null
                            ? `${Math.round(q.preQuizStats.pctCorrect)}% pre-quiz`
                            : 'No data'}
                        </span>
                      </div>
                      <div className="question-text" dangerouslySetInnerHTML={{ __html: q.question }} />
                      <div className="question-options">
                        {q.options.map((opt, i) => (
                          <span key={i} className="option-preview">
                            {String.fromCharCode(65 + i)}){' '}
                            <span dangerouslySetInnerHTML={{ __html: opt }} />
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flash-settings">
                  <label>
                    Duration:
                    <select value={duration} onChange={e => setDuration(Number(e.target.value))}>
                      <option value={30}>30 seconds</option>
                      <option value={45}>45 seconds</option>
                      <option value={60}>60 seconds</option>
                      <option value={90}>90 seconds</option>
                      <option value={120}>2 minutes</option>
                    </select>
                  </label>
                </div>

                <div className="flash-actions">
                  <button className="cancel-btn" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className="flash-btn"
                    onClick={flashQuestion}
                    disabled={!selectedQuestion || loading}
                  >
                    Flash to Class
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Active Session View */}
        {viewMode === 'active' && activeSession && (
          <div className="flash-quiz-content active-session">
            <div className="session-timer">
              <span className={`time ${remainingTime < 10 ? 'urgent' : ''}`}>
                {formatTime(remainingTime)}
              </span>
              <span className="timer-label">remaining</span>
            </div>

            <div className="active-question">
              <div className="question-text" dangerouslySetInnerHTML={{ __html: activeSession.question }} />
              {activeSession.concept && (
                <div className="question-concept">Concept: {activeSession.concept}</div>
              )}
            </div>

            {liveResults && (
              <div className="live-stats">
                <div className="stat-row">
                  <span className="stat-label">Responses</span>
                  <span className="stat-value">
                    {liveResults.totalResponses} / {liveResults.totalStudents}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Response Rate</span>
                  <span className="stat-value">{Math.round(liveResults.responseRate)}%</span>
                </div>

                <div className="options-results">
                  {activeSession.options.map((opt, i) => {
                    const optionLetter = String.fromCharCode(65 + i);
                    const count = liveResults.optionCounts[opt] || 0;
                    const pct =
                      liveResults.totalResponses > 0
                        ? (count / liveResults.totalResponses) * 100
                        : 0;

                    return (
                      <div key={i} className="option-result">
                        <span className="option-letter">{optionLetter}</span>
                        <span className="option-text" dangerouslySetInnerHTML={{ __html: opt }} />
                        <div className="option-bar">
                          <div className="bar-fill" style={{ width: `${pct}%` }}></div>
                        </div>
                        <span className="option-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="session-actions">
              <button className="close-quiz-btn" onClick={closeQuiz} disabled={loading}>
                {loading ? 'Closing...' : 'Close Quiz & Show Results'}
              </button>
            </div>
          </div>
        )}

        {/* Results View */}
        {viewMode === 'results' && liveResults && (
          <div className="flash-quiz-content results-view">
            <div className="results-summary">
              <div className="result-stat">
                <span className="value">{liveResults.totalResponses}</span>
                <span className="label">Responses</span>
              </div>
              <div className="result-stat">
                <span className="value">{Math.round(liveResults.responseRate)}%</span>
                <span className="label">Response Rate</span>
              </div>
              <div className="result-stat correct">
                <span className="value">{liveResults.correctCount}</span>
                <span className="label">Correct</span>
              </div>
              <div className="result-stat">
                <span className="value">{Math.round(liveResults.inClassPctCorrect)}%</span>
                <span className="label">In-Class Correct</span>
              </div>
            </div>

            {liveResults.preQuizPctCorrect !== null && (
              <div className="comparison-box">
                <div className="comparison-header">Pre-Quiz vs In-Class Comparison</div>
                <div className="comparison-stats">
                  <div className="compare-stat">
                    <span className="label">Pre-Quiz</span>
                    <span className="value">{Math.round(liveResults.preQuizPctCorrect)}%</span>
                  </div>
                  <div className="compare-arrow">
                    {liveResults.improvement !== null && liveResults.improvement > 0 ? (
                      <span className="improvement positive">+{Math.round(liveResults.improvement)}%</span>
                    ) : liveResults.improvement !== null ? (
                      <span className="improvement negative">{Math.round(liveResults.improvement)}%</span>
                    ) : (
                      <span className="improvement">-</span>
                    )}
                  </div>
                  <div className="compare-stat">
                    <span className="label">In-Class</span>
                    <span className="value">{Math.round(liveResults.inClassPctCorrect)}%</span>
                  </div>
                </div>
              </div>
            )}

            <div className="results-question">
              <div className="question-text" dangerouslySetInnerHTML={{ __html: liveResults.question }} />
              {liveResults.concept && (
                <div className="question-concept">Concept: {liveResults.concept}</div>
              )}
            </div>

            <div className="final-options-results">
              {liveResults.options.map((opt, i) => {
                const optionLetter = String.fromCharCode(65 + i);
                const count = liveResults.optionCounts[opt] || 0;
                const pct =
                  liveResults.totalResponses > 0
                    ? (count / liveResults.totalResponses) * 100
                    : 0;
                const isCorrect = opt === liveResults.correctAnswer;

                return (
                  <div key={i} className={`option-result ${isCorrect ? 'correct' : ''}`}>
                    <span className="option-letter">{optionLetter}</span>
                    <div className="option-text" dangerouslySetInnerHTML={{ __html: opt }} />
                    <div className="option-bar">
                      <div
                        className={`bar-fill ${isCorrect ? 'correct' : ''}`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                    <span className="option-count">
                      {count} ({Math.round(pct)}%)
                    </span>
                    {isCorrect && <span className="correct-badge">Correct</span>}
                  </div>
                );
              })}
            </div>

            <div className="results-actions">
              <button className="flash-another-btn" onClick={resetToQuestions}>
                Flash Another Question
              </button>
              <button className="done-btn" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}

        {/* History View */}
        {viewMode === 'history' && (
          <div className="flash-quiz-content history-view">
            <button className="back-btn" onClick={resetToQuestions}>
              Back to Questions
            </button>

            {history.length === 0 ? (
              <div className="no-history">No flash quizzes have been run for this session yet.</div>
            ) : (
              <div className="history-list">
                {history.map((item, idx) => (
                  <div key={item.sessionId || idx} className="history-item">
                    <div className="history-question" dangerouslySetInnerHTML={{ __html: item.question }} />
                    <div className="history-stats">
                      <span>Responses: {item.totalResponses}</span>
                      <span>Correct: {item.correctCount}</span>
                      <span>{Math.round(item.pctCorrect)}% correct</span>
                    </div>
                    <div className="history-time">
                      {new Date(item.startedAt || item.StartedAt || item.CreatedAt).toLocaleString(undefined, {
                        day: 'numeric', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashQuizManager;
